import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Skeleton, Tag, Typography } from "@douyinfe/semi-ui";
import dayjs from "dayjs";
import {
  AiActionTrack,
  AiCoachApi,
  AiCoachGenerateReq,
  AiReport,
} from "../../api/aiCoach";
import { ApiClient } from "../../api/ApiClient";
import ActionModal from "./ActionModal";
import ReportHeader, { SaleOption } from "./components/ReportHeader";
import MetricsGrid from "./components/MetricsGrid";
import DiagnosisPanel from "./components/DiagnosisPanel";
import CustomerDiagnosisList from "./components/CustomerDiagnosisList";
import ResistanceList from "./components/ResistanceList";
import AdjustmentCards from "./components/AdjustmentCards";
import ActionBoard from "./components/ActionBoard";
import BreakdownDrawer from "./components/BreakdownDrawer";
import { ProcessingView, EmptyView, FailedView } from "./components/StatusViews";
import { PeriodPreset, tagColor } from "./constants";
import { pageWrap, sp } from "./styles";

const { Text } = Typography;

const periodRange = (preset: PeriodPreset) => {
  const base = dayjs();
  const dayOfWeek = base.day() === 0 ? 7 : base.day();
  const thisMonday = base.subtract(dayOfWeek - 1, "day");
  const start = preset === "thisWeek" ? thisMonday : thisMonday.subtract(7, "day");
  const end = start.add(6, "day");
  return { start, end };
};

const fmtDate = (d: dayjs.Dayjs) => d.format("YYYY-MM-DD");

const AiWeeklyReportPage: React.FC = () => {
  const role = localStorage.getItem("role") ?? "";
  const isAdmin = role === "root" || role === "op_manager" || role === "quzhang";

  const [period, setPeriod] = useState<PeriodPreset>("thisWeek");
  const [report, setReport] = useState<AiReport | null>(null);
  const [openActions, setOpenActions] = useState<AiActionTrack[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [actionModalIdx, setActionModalIdx] = useState<number | null>(null);
  /** 哨兵：ActionBoard 接受新行动/改状态后 +1，触发其内部 reload */
  const [actionBoardKey, setActionBoardKey] = useState(0);
  /** 当前报告已接受的 adjustmentIndex 集合，给 AdjustmentCards 标记"已接受" */
  const [acceptedIndexes, setAcceptedIndexes] = useState<number[]>([]);
  /** 指标/抗拒点明细 Drawer 状态 */
  const [breakdown, setBreakdown] = useState<{
    visible: boolean;
    title: string;
    mode: "metric" | "resistance";
    payload: string;
  }>({ visible: false, title: "", mode: "metric", payload: "" });
  const pollRef = useRef<number | null>(null);
  const pollCountRef = useRef(0);

  const [saleOptions, setSaleOptions] = useState<SaleOption[]>([]);
  const [selectedSaleId, setSelectedSaleId] = useState<number | undefined>(undefined);

  const range = useMemo(() => periodRange(period), [period]);

  useEffect(() => {
    if (!isAdmin) return;
    const api = ApiClient.getDefault();
    api
      .request<{ records: SaleOption[] }>("/users", "GET", {
        page: 1,
        size: 200,
        role: "sale",
        status: "ENABLED",
      })
      .then((r) => {
        const list = r.data?.records ?? [];
        setSaleOptions(list);
        if (list.length > 0) setSelectedSaleId(list[0].id);
      })
      .catch(() => {
        /* ignore */
      });
  }, [isAdmin]);

  const loadOpenActions = useCallback(async () => {
    if (isAdmin && selectedSaleId === undefined) return;
    try {
      const r = await AiCoachApi.listActions(selectedSaleId);
      setOpenActions(r.data ?? []);
    } catch {
      /* ignore */
    }
  }, [isAdmin, selectedSaleId]);

  const fetchCurrent = useCallback(
    async (dateStr: string) => {
      if (isAdmin && selectedSaleId === undefined) {
        setLoading(false);
        return;
      }
      try {
        const resp = await AiCoachApi.getCurrentReport(dateStr, selectedSaleId);
        setReport(resp.data ?? null);
      } finally {
        setLoading(false);
      }
    },
    [isAdmin, selectedSaleId]
  );

  const stopPoll = useCallback(() => {
    if (pollRef.current) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const poll = useCallback(
    (reportId: number) => {
      stopPoll();
      pollCountRef.current = 0;
      // LLM 调用最长 240s（后端 future timeout），poll 给到 5 分钟兜底。
      // 原先 30 次 / 60s 太短，LLM 慢时前端先停 → UI 卡在 processing。
      pollRef.current = window.setInterval(async () => {
        pollCountRef.current += 1;
        try {
          const r = await AiCoachApi.getReport(reportId);
          setReport(r.data);
          if (r.data.status !== "processing" || pollCountRef.current >= 150) stopPoll();
        } catch {
          stopPoll();
        }
      }, 2000);
    },
    [stopPoll]
  );

  useEffect(() => {
    if (isAdmin && selectedSaleId === undefined) return;
    setLoading(true);
    fetchCurrent(fmtDate(range.end));
    return () => stopPoll();
  }, [fetchCurrent, range.end, stopPoll, isAdmin, selectedSaleId]);

  useEffect(() => {
    loadOpenActions();
  }, [loadOpenActions]);

  const handleGenerate = async () => {
    if (isAdmin && selectedSaleId === undefined) return;
    setGenerating(true);
    try {
      const req: AiCoachGenerateReq = {
        periodStart: fmtDate(range.start),
        periodEnd: fmtDate(range.end),
        ...(isAdmin && selectedSaleId ? { salesId: selectedSaleId } : {}),
        // 已有报告（含 processing/ready/failed）时点"重新生成"，强制覆盖重跑，
        // 否则后端会按 60s/READY 复用策略直接返回旧报告，看起来像"卡住"。
        ...(report ? { force: true } : {}),
      };
      const resp = await AiCoachApi.generateReport(req);
      const r = await AiCoachApi.getReport(resp.data.reportId);
      setReport(r.data);
      if (r.data.status === "processing") poll(r.data.id);
    } catch {
      /* Toast handled by ApiClient */
    } finally {
      setGenerating(false);
    }
  };

  const isProcessing = report?.status === "processing" || generating;
  const disableGenerate = isProcessing || (isAdmin && selectedSaleId === undefined);

  const periodLabel = report
    ? `周期 ${report.periodStart} ~ ${report.periodEnd}`
    : `周期 ${fmtDate(range.start)} ~ ${fmtDate(range.end)}（尚未生成）`;

  const openActionCount = openActions.filter(
    (a) => a.status !== "done" && a.status !== "rejected"
  ).length;

  const renderBody = () => {
    if (loading) {
      return (
        <div style={{ padding: sp.xl }}>
          <Skeleton.Title />
          <Skeleton.Paragraph rows={6} />
        </div>
      );
    }
    if (!report) return <EmptyView onGenerate={handleGenerate} />;
    if (report.status === "failed") {
      return <FailedView message={report.errorMessage} onRetry={handleGenerate} />;
    }
    if (report.status === "processing") return <ProcessingView />;
    if (report.status === "ready" && report.metricsJson && report.llmDiagnosis) {
      const m = report.metricsJson;
      const d = report.llmDiagnosis;
      return (
        <>
          {report.triggeredTags && report.triggeredTags.length > 0 && (
            <div
              style={{
                marginBottom: sp.lg,
                padding: sp.md,
                background: "var(--semi-color-warning-light-default)",
                borderRadius: 4,
                display: "flex",
                alignItems: "center",
                gap: sp.sm,
                flexWrap: "wrap",
              }}
            >
              <Text strong style={{ color: "var(--semi-color-warning)" }}>
                触发标签：
              </Text>
              {report.triggeredTags.map((t) => (
                <Tag key={t} color={tagColor(t)} size="large">
                  {t}
                </Tag>
              ))}
            </div>
          )}

          <MetricsGrid
            metrics={m}
            onBreakdown={(key, label) =>
              setBreakdown({ visible: true, title: `${label} · 客户明细`, mode: "metric", payload: key })
            }
          />
          <DiagnosisPanel
            diagnosis={d}
            totalCustomers={m.totalCustomers}
            analyzedCustomers={m.analyzedCustomers}
          />
          {d.customerDiagnoses && d.customerDiagnoses.length > 0 && (
            <CustomerDiagnosisList items={d.customerDiagnoses} />
          )}
          <ResistanceList
            items={d.topResistances}
            onBreakdown={(term) =>
              setBreakdown({ visible: true, title: `抗拒点：${term}`, mode: "resistance", payload: term })
            }
          />
          <AdjustmentCards
            adjustments={d.adjustments}
            acceptedIndexes={acceptedIndexes}
            onAccept={(i) => setActionModalIdx(i)}
          />
          <ActionBoard
            reportId={report.id}
            refreshKey={actionBoardKey}
            onAcceptedIndexes={setAcceptedIndexes}
            onChanged={loadOpenActions}
          />
        </>
      );
    }
    return null;
  };

  return (
    <div style={pageWrap}>
      <ReportHeader
        isAdmin={isAdmin}
        saleOptions={saleOptions}
        selectedSaleId={selectedSaleId}
        onSelectSale={setSelectedSaleId}
        period={period}
        onChangePeriod={setPeriod}
        report={report}
        periodLabel={periodLabel}
        isProcessing={isProcessing}
        disableGenerate={disableGenerate}
        onGenerate={handleGenerate}
      />

      {openActionCount > 0 && (
        <div
          style={{
            marginBottom: sp.lg,
            padding: `${sp.sm} ${sp.md}`,
            background: "var(--semi-color-warning-light-default)",
            borderRadius: 4,
            borderLeft: "3px solid var(--semi-color-warning)",
          }}
        >
          <Text>
            🔔 有 <Text strong>{openActionCount}</Text> 个未完成行动，请关注
          </Text>
        </div>
      )}

      {renderBody()}

      {actionModalIdx !== null && report && (
        <ActionModal
          reportId={report.id}
          adjustmentIndex={actionModalIdx}
          defaultAction={report.llmDiagnosis?.adjustments?.[actionModalIdx]?.action ?? ""}
          onClose={() => setActionModalIdx(null)}
          onSaved={() => {
            setActionModalIdx(null);
            setActionBoardKey((k) => k + 1);
            loadOpenActions();
          }}
        />
      )}

      {report && (
        <BreakdownDrawer
          visible={breakdown.visible}
          title={breakdown.title}
          reportId={report.id}
          mode={breakdown.mode}
          payload={breakdown.payload}
          onClose={() => setBreakdown((s) => ({ ...s, visible: false }))}
        />
      )}
    </div>
  );
};

export default AiWeeklyReportPage;
